import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import { getBuyplanDetails } from "actions/buyplanDetailsActions.js";
import { getBuyplanTotals } from "actions/buyplanTotalsActions.js";
import { getFilters } from "actions/filterActions.js";
import { deleteBuyplanItem, fetchParentProducts } from "actions/sparePartActions.js";
import { apiBaseUrl } from "apis/apiHelpers.js";
import { addNotification } from "notificationStore.js";
import CustomModal from "components/CustomModal";
import ParentProductsGrid from "components/SparePart/ParentProductsGrid.jsx";
import DiscountedPriceCell from "components/SparePart/DiscountedPriceCell.jsx";
import { StyledBuyplanDetailsContainer } from "./BuyplanDetailsContainer.styles";

import {
  buyplanIdSelector
} from "selectors/buyplanIdSelector.js";
import {
  isLoadingSelector,
  buyplanDetailsDataSelector
} from "selectors/buyplanDetailsSelector.js";
import { isDataLoadingSelector, appliedFiltersSelector } from "selectors/filtersSelector";
import { warehouseIdSelector } from "selectors/warehouseSelector.js";
import { parentProductsSelector } from "selectors/sparePartSelector.js";

import Loader from "components/Loader/Loader.jsx";

import { BUYPLAN } from "constants.js";
import i18next from "i18next";

const getBuyplanDetailListLoader = () => <Loader />;

// Pulls the first { quantity, bpsb_id } leaf out of a qty_grid month's attr_rows
// (spare-part buyplan items only ever have a single attribute/size combo, so
// "first" is "the" entry). Used to read the pre-order month's backorder
// quantity/bpsb_id, which the buyplan_items response nests under
// qty_grid.pre_order_delivery_month rather than exposing as flat fields.
const getFirstAttrRowLeaf = attrRows => {
  if (!attrRows) return null;
  for (const attrKey of Object.keys(attrRows)) {
    const sizes = attrRows[attrKey] || {};
    for (const sizeKey of Object.keys(sizes)) {
      return sizes[sizeKey];
    }
  }
  return null;
};

// The first leaf (across all regular delivery_months, i.e. excluding
// pre_order_delivery_month) that has a bpsb_id — this is the item's regular
// (non-backorder) BuyplanSizeBreak, distinct from the pre-order one.
const getRegularLeaf = qtyGrid => {
  if (!qtyGrid || !qtyGrid.delivery_months) return null;
  for (const month of qtyGrid.delivery_months) {
    const leaf = getFirstAttrRowLeaf(month.attr_rows);
    if (leaf && leaf.bpsb_id) return leaf;
  }
  return null;
};

const findBpsbId = obj => {
  if (!obj || typeof obj !== "object") return null;
  if (obj.bpsb_id) return obj.bpsb_id;
  if (obj.buyplan_size_break_id) return obj.buyplan_size_break_id;
  if (Array.isArray(obj)) {
    for (const item of obj) { const found = findBpsbId(item); if (found) return found; }
  } else {
    for (const key of Object.keys(obj)) { const found = findBpsbId(obj[key]); if (found) return found; }
  }
  return null;
};

// The add_item endpoint requires product_part_relationship_id to resolve/claim a
// spare-part's BuyplanSizeBreak — without it, add_item can't tell this is a spare-parts
// request and (since the buyplan_item already exists) fails with "Record Already Exist"
// instead of creating/finding the BSB. Every leaf of a spare-part item's qty_grid
// (regular or backorder) carries the same product_part_relationship_id, so pull it
// from wherever it's already stamped.
const findPprId = obj => {
  if (!obj || typeof obj !== "object") return null;
  if (obj.product_part_relationship_id) return obj.product_part_relationship_id;
  if (Array.isArray(obj)) {
    for (const item of obj) { const found = findPprId(item); if (found) return found; }
  } else {
    for (const key of Object.keys(obj)) { const found = findPprId(obj[key]); if (found) return found; }
  }
  return null;
};
const WsPriceCellRenderer = ({ data }) => (
  <DiscountedPriceCell
    originalPrice={data.display_wholesale_price}
    discountedPrice={data.discounted_display_wholesale_price}
    isDiscounted={data.is_discounted}
    isPbDiscounted={data.pb_discounted}
  />
);
WsPriceCellRenderer.displayName = "WsPriceCellRenderer";

const ActionsCellRenderer = (props) => {
  const isSelected = props.context.selectedRowId === props.data.id;

  const handleDelete = () => {
    const buyplanId = props.context.buyplanId;
    const itemId = props.data.id;

    if (!buyplanId || !itemId) {
      addNotification({ type: "error", message: i18next.t('sparePart.errors.missingBuyplanId') });
      return;
    }

    // Dispatch action to delete via saga
    props.context.dispatch(deleteBuyplanItem(buyplanId, itemId));
  };

  const handleView = () => {
    const buyplanId = props.context.buyplanId;
    const brandId = props.data.brand_id;
    const itemId = props.data.id;

    // Set this row as selected
    props.context.setSelectedRowId(itemId);

    // Extract bpsb_id from qty_grid nested structure
    let bpsbId = null;
    const qtyGrid = props.data.qty_grid;
    
    if (qtyGrid && qtyGrid.delivery_months) {
      const months = qtyGrid.delivery_months || [];
      for (const month of months) {
        const rows = month.attr_rows || {};
        for (const attrKey in rows) {
          for (const sizeKey in rows[attrKey]) {
            if (rows[attrKey][sizeKey].bpsb_id) {
              bpsbId = rows[attrKey][sizeKey].bpsb_id;
              break;
            }
          }
          if (bpsbId) break;
        }
        if (bpsbId) break;
      }
    }

    if (!buyplanId || !bpsbId) {
      addNotification({ type: "error", message: i18next.t('sparePart.errors.missingBpsbId') });
      return;
    }

    // Dispatch action to fetch parent products via saga
    props.context.dispatch(fetchParentProducts(buyplanId, bpsbId, brandId));
    props.context.setShowParentProductsModal(true);
  };

  return (
    <div className="actions-cell">
      <button
        className="btn btn-sm btn-outline-secondary me-2"
        title="Delete"
        onClick={handleDelete}
      >
        <i className="fa fa-trash"></i>
      </button>
      <button
        className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
        title="View"
        onClick={handleView}
      >
        <i className="fa fa-eye"></i>
      </button>
    </div>
  );
};

const SparePartBuyplanDetailsContainer = () => {
  const dispatch = useDispatch();
  const { releaseId } = useParams();

  const buyplanId = useSelector(buyplanIdSelector);
  const warehouseId = useSelector(warehouseIdSelector);
  const isLoading = useSelector(isLoadingSelector);
  const buyplanDetails = useSelector(buyplanDetailsDataSelector);
  const appliedFilters = useSelector(appliedFiltersSelector);
  const isDataLoading = useSelector(isDataLoadingSelector);
  const [showParentProductsModal, setShowParentProductsModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const parentProductsData = useSelector(parentProductsSelector);

  useEffect(() => {
    if (buyplanId) {
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get("code");

      dispatch(getFilters(releaseId, BUYPLAN, buyplanId, code));
    }
  }, [dispatch, buyplanId, releaseId]);

  useEffect(() => {
    if (buyplanId && isDataLoading === false) {
      dispatch(
        getBuyplanDetails(
          buyplanId,
          warehouseId,
          appliedFilters.toJS(),
          999999,
          0,
          true
        )
      );
    }
  }, [dispatch, buyplanId, appliedFilters, warehouseId, isDataLoading]);

  const columnDefs = useMemo(() => [
    {
      headerName: i18next.t('sparePart.grid.columns.partDescription'),
      flex: 2,
      minWidth: 180,
      cellClass: 'part-description-cell',
      valueGetter: (params) => params.data.name || '',
      tooltipValueGetter: (params) => params.data.name || '',
    },
    {
      field: 'sku',
      headerName: i18next.t('sparePart.grid.columns.partSku'),
      flex: 1,
      minWidth: 100,
    },
    {
      colId: 'wholesale_price_display',
      headerName: i18next.t('sparePart.grid.columns.wsPrice'),
      flex: 1,
      minWidth: 90,
      valueGetter: (params) => {
        // Show discounted price if available, otherwise show regular price
        return params.data.discounted_display_wholesale_price || params.data.display_wholesale_price;
      },
      cellRenderer: WsPriceCellRenderer,
    },
    {
      field: 'retail_price_formatted',
      headerName: i18next.t('sparePart.grid.columns.rrPrice'),
      flex: 1,
      minWidth: 90,
    },
    {
      field: 'total_quantity',
      headerName: i18next.t('sparePart.grid.columns.quantity'),
      editable: true,
      flex: 1,
      minWidth: 100,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        min: 0,
        precision: 0,
        maxLength: 5,
      },
      valueParser: (params) => {
        const value = Number(params.newValue);
        if (isNaN(value)) return 0;
        // Reset to 0 if value exceeds 5 digits
        const maxValue = 99999;
        if (value > maxValue) {
          addNotification({
            type: "error",
            message: "Please enter Qtys between 0 - 99999"
          });
          return 0;
        }
        return value;
      },
    },
    {
      field: 'backorder_quantity',
      headerName: i18next.t('sparePart.grid.columns.backorderQuantity'),
      // backorder_quantity/backorder_bsb_id are derived from
      // qty_grid.pre_order_delivery_month (see the rowData useEffect below) — a
      // separate BuyplanSizeBreak from the regular quantity's. When the item has
      // no pre-order BuyplanSizeBreak yet, onCellValueChanged creates one on the
      // first edit. Editability mirrors ProductsListContainer's spare-part grid:
      // only when the item's brand/release allow backorder at all, it has a lead
      // time, and that lead time is positive.
      editable: (params) =>
        !!params.data.is_backorder_allowed &&
        !!params.data.has_lead_time &&
        Number(params.data.pre_order_lead_time_days) > 0,
      cellStyle: (params) => {
        if (!params.data.is_backorder_allowed || !params.data.has_lead_time) {
          return { background: "#f5f5f5", color: "#ccc", cursor: "not-allowed" };
        }
        if (!(Number(params.data.pre_order_lead_time_days) > 0)) {
          return { background: "#f5f5f5", color: "#ccc", cursor: "not-allowed" };
        }
        return {};
      },
      flex: 1,
      minWidth: 130,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        min: 0,
        precision: 0,
      },
      valueGetter: (params) => {
        if (!params.data.is_backorder_allowed || !params.data.has_lead_time) return "";
        const qty = params.data.backorder_quantity;
        return qty !== null && qty !== undefined && Number(qty) !== 0 ? qty : "∞";
      },
      valueParser: (params) => {
        const value = Number(params.newValue);
        return isNaN(value) ? 0 : value;
      },
    },
    {
      field: 'total_price_formatted',
      headerName: i18next.t('sparePart.grid.columns.subtotal'),
      flex: 1,
      minWidth: 90,
    },
    {
      field: 'actions',
      headerName: i18next.t('sparePart.grid.columns.action'),
      cellRenderer: ActionsCellRenderer,
      flex: 1,
      minWidth: 110,
      sortable: false,
      filter: false,
    },
  ], []);

  // Default column properties
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: false,
    resizable: true,
    unSortIcon: true,
  }), []);

  const buyplanItems = buyplanDetails && buyplanDetails.get("buyplan_items");

  // Local rowData, resynced from redux only when buyplan_items itself is refetched
  // (e.g. initial load, filter change) — NOT on every unrelated buyplanDetails update
  // such as the totals refresh that follows a quantity edit. This lets a quantity
  // edit update the grid in place instead of the whole table flashing/reloading.
  const [rowData, setRowData] = useState([]);

  useEffect(() => {
    if (!buyplanItems) { setRowData([]); return; }
    setRowData(buyplanItems.toJS().map(item => {
      const regularLeaf = getRegularLeaf(item.qty_grid);
      const preOrderMonth = item.qty_grid && item.qty_grid.pre_order_delivery_month;
      const preOrderLeaf = getFirstAttrRowLeaf(preOrderMonth && preOrderMonth.attr_rows);
      const leafQuantity = leaf =>
        leaf && leaf.quantity !== "" && leaf.quantity !== null && leaf.quantity !== undefined
          ? Number(leaf.quantity)
          : 0;
      return {
        ...item,
        // The API's flat total_quantity is regular + backorder combined. Keep this
        // column scoped to the regular delivery months only, so it never doubles
        // as (or gets overwritten by) the backorder figure.
        total_quantity: leafQuantity(regularLeaf),
        backorder_quantity: leafQuantity(preOrderLeaf),
        backorder_bsb_id: preOrderLeaf ? preOrderLeaf.bpsb_id : null,
      };
    }));
  }, [buyplanItems]);

  const onCellValueChanged = async (params) => {
    // Ignore the change event fired by this handler's own revert-on-failure
    // (params.node.setDataValue below) — otherwise the revert re-enters this
    // handler, fails the same way, reverts again, and loops forever.
    if (params.source === 'revert') return;

    // Skip if no actual change
    if (params.oldValue === params.newValue) {
      return;
    }

    const field = params.colDef.field;
    if (field !== 'total_quantity' && field !== 'backorder_quantity') {
      return;
    }

    const newQuantity = params.newValue.toString();
    const oldValue = params.oldValue;
    const itemId = params.data.id;
    const authToken = sessionStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/brandscope.com; version=1',
      'warehouse_id': warehouseId || '',
      'Authorization': authToken ? `Bearer ${authToken}` : ''
    };

    // Optimistic update — reflect the new value immediately so the grid
    // updates in place instead of waiting on (and flashing from) a full reload.
    setRowData(prev =>
      prev.map(row => (row.id === itemId ? { ...row, [field]: params.newValue } : row))
    );

    try {
      // total_quantity persists against the regular delivery months' bpsb_id;
      // backorder_quantity against the pre-order bpsb_id derived in the rowData
      // useEffect above — these are two distinct BuyplanSizeBreaks.
      let bpsbId = field === 'backorder_quantity'
        ? params.data.backorder_bsb_id
        : (getRegularLeaf(params.data.qty_grid) || {}).bpsb_id;

      if (!bpsbId) {
        // Either BuyplanSizeBreak (regular or pre-order) may not exist yet for this
        // item — create/claim it (idempotent) via the same add_item endpoint the
        // Products tab uses. add_item only recognizes this as a spare-parts request
        // (and only bypasses "already exists" for an item already on the buyplan)
        // when product_part_relationship_id is supplied, so it must come along here.
        const pprId = findPprId(params.data.qty_grid);
        if (!pprId) {
          throw new Error(i18next.t('sparePart.errors.missingRequiredData'));
        }
        const addRes = await fetch(`${apiBaseUrl || ''}/api/buyplans/${buyplanId}/items.json`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            product_release_id: params.data.product_release_id,
            product_part_relationship_id: pprId,
            warehouse_id: warehouseId || '',
            is_pre_order: field === 'backorder_quantity'
          })
        });
        let addData = {};
        try { addData = await addRes.json(); } catch (_) { /* ignore */ }
        if (!addRes.ok) {
          const errText = (addData.errors && addData.errors[0]) || addData.error || '';
          const alreadyExists =
            addRes.status === 422 ||
            addRes.status === 409 ||
            (typeof errText === 'string' && /already|taken|exist/i.test(errText));
          if (!alreadyExists) throw new Error(errText || `add_item failed: ${addRes.status}`);
        }
        bpsbId = findBpsbId(addData);
        if (!bpsbId) throw new Error(i18next.t('sparePart.errors.missingRequiredData'));
      }

      const updateUrl = `${apiBaseUrl || ''}/api/buyplans/${buyplanId}/size_breaks/${bpsbId}/update_quantity.json`;

      const response = await fetch(updateUrl, {
        method: 'PUT',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          quantity: newQuantity,
          wholesale_price: '',
          'warehouse_id': warehouseId || ''
        })
      });

      if (!response.ok) {
        let errData = {};
        try { errData = await response.json(); } catch (_) { /* ignore */ }
        const errText =
          (Array.isArray(errData.errors) && errData.errors[0]) ||
          errData.error ||
          i18next.t('sparePart.errors.updateFailed');
        throw new Error(errText);
      }

      // The new pre-order bpsb_id now exists server-side — keep it on the row
      // so a subsequent backorder edit updates it directly instead of
      // re-creating it.
      if (field === 'backorder_quantity' && !params.data.backorder_bsb_id) {
        setRowData(prev =>
          prev.map(row => (row.id === itemId ? { ...row, backorder_bsb_id: bpsbId } : row))
        );
      }

      // Update totals in header (no full details re-fetch needed — the
      // optimistic update above already shows the correct quantity).
      dispatch(getBuyplanTotals(buyplanId));

    } catch (error) {
      addNotification({
        type: "error",
        message: (error && error.message) || i18next.t('sparePart.errors.updateFailed')
      });
      // Revert the optimistic update on failure
      setRowData(prev =>
        prev.map(row => (row.id === itemId ? { ...row, [field]: oldValue } : row))
      );
      params.node.setDataValue(field, oldValue, 'revert');
    }
  };

  const loadedBuyplanDetailsCount = buyplanItems ? buyplanItems.size : 0;

  return (
    <StyledBuyplanDetailsContainer>
      {isLoading && getBuyplanDetailListLoader()}

      {!isLoading && loadedBuyplanDetailsCount === 0 && (
        <div className="d-flex justify-content-center align-items-center h-100">
          <p>{i18next.t("buyplanItem.listing")}</p>
        </div>
      )}

      {!isLoading && loadedBuyplanDetailsCount > 0 && (
        <div className="grid-wrapper">
          <div className="ag-theme-alpine spare-parts-grid">
            <AgGridReact
              rowData={rowData}
              getRowId={params => String(params.data.id)}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onCellValueChanged={onCellValueChanged}
              context={{
                buyplanId: buyplanId,
                dispatch: dispatch,
                warehouseId: warehouseId,
                appliedFilters: appliedFilters.toJS(),
                setShowParentProductsModal: setShowParentProductsModal,
                selectedRowId: selectedRowId,
                setSelectedRowId: setSelectedRowId
              }}
              animateRows={true}
              rowSelection='multiple'
              suppressRowClickSelection={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              rowHeight={35}
              headerHeight={38}
            />
          </div>
          <div className="grid-footer">
            <span className="total-rows-label">{i18next.t('sparePart.grid.footer.totalItems')} <strong>{rowData.length}</strong></span>
          </div>
        </div>
      )}

      {/* Parent Products Modal */}
      {showParentProductsModal && (
        <CustomModal
          isOpen={showParentProductsModal}
          closeModalCallback={() => {
            setShowParentProductsModal(false);
            setSelectedRowId(null); // Clear selection when modal closes
          }}
          title={i18next.t('sparePart.modal.parentProducts')}
          modalSize="lg"
          body={
            <div className="parent-products-modal-body ag-theme-alpine">
              <ParentProductsGrid data={parentProductsData ? parentProductsData.toJS() : []} />
            </div>
          }
        />
      )}
    </StyledBuyplanDetailsContainer>
  );
};

export default React.memo(SparePartBuyplanDetailsContainer);
